import React, { useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";

/**
 * Insight loss for one initiative — did the problems it targeted go away?
 *
 * Kept deliberately small. An earlier version put four KPI tiles, a seven-column
 * table, a chart and six paragraphs of method on screen at once, which buried the
 * two numbers anyone actually reads: how much loss went away, and which insight
 * it came from. Everything else moved behind "Details".
 *
 * Three things are NOT simplified away, because each one prevents a specific
 * misreading rather than merely adding information:
 *
 *   * The table shows PER-DAY figures. The two windows are different lengths, so
 *     their totals routinely move the opposite way from the truth — a real case
 *     here reads 104,491 -> 308,995 (+196%) as totals while the daily rate falls
 *     3,603 -> 3,322 (-7.8%). Totals stay in Details, labelled as not comparable.
 *   * Avoided risk is never a savings tile. Eliminating an insight also drops its
 *     equipment/maintenance exposure (~1M PKR per detection), which is a risk
 *     figure, not money saved. It sits in Details as one sentence.
 *   * An insight whose detector failed says so. It never renders as zero, because
 *     zero loss reads as a perfect result.
 */

const PKR = new Intl.NumberFormat("en-PK", { maximumFractionDigits: 0 });
const NUM = new Intl.NumberFormat("en-PK", { maximumFractionDigits: 1 });

const fmtPkr = (v) => (v == null ? "—" : `PKR ${PKR.format(v)}`);
const fmtPct = (v) => (v == null ? "—" : `${NUM.format(v)}%`);

/** Signed, so a regression is unmistakable. */
const fmtSigned = (v) => {
  if (v == null) return "—";
  return `${v > 0 ? "+" : v < 0 ? "−" : ""}PKR ${PKR.format(Math.abs(v))}`;
};

const STATUS_META = {
  resolved: { label: "Resolved", tone: "good" },
  improved: { label: "Improved", tone: "good" },
  unchanged: { label: "Unchanged", tone: "flat" },
  worsened: { label: "Worsened", tone: "bad" },
  emerged: { label: "New", tone: "bad" },
  absent: { label: "Not detected", tone: "flat" },
  unknown: { label: "Not measured", tone: "flat" },
};

/**
 * Colour class for a loss figure. THREE states, not two.
 *
 * The row badge already models this correctly -- STATUS_META maps `absent` and
 * `unchanged` to tone "flat" -- but the money and percent cells beside it used a
 * bare `value > 0 ? good : bad`, which has no flat state. So an insight that was
 * never detected got a neutral grey badge saying "Not detected" and, on the same
 * row, PKR 0 in alarm red. Nothing was wrong with the equipment; the panel just had
 * no way to say so.
 *
 * Zero here means "no loss to recover", which is the best outcome available, not
 * the worst. It is rendered neutral rather than green because there was also no
 * improvement to claim credit for.
 */
function toneClass(value) {
  const v = value || 0;
  if (v > 0) return "ei-good";
  if (v < 0) return "ei-bad";
  return "ei-flat";
}

export default function InitiativeInsightLoss({ insightLoss }) {
  const [showDetails, setShowDetails] = useState(false);

  // Nulls pass through untouched: ApexCharts breaks the line at a null, which is
  // the right rendering for "the detector failed for this bucket". A zero there
  // would draw as an improvement that never happened.
  const chart = useMemo(() => {
    const trends = insightLoss?.trends;
    if (!trends?.available) return null;
    const toPoints = (rows) =>
      (rows || []).map((p) => ({
        x: new Date(p.period).getTime(),
        y: p.production_loss_pkr_per_day,
      }));
    const before = toPoints(trends.baseline);
    const after = toPoints(trends.after);
    if (!before.length && !after.length) return null;

    return {
      series: [
        { name: "Before", data: before },
        { name: "After", data: after },
      ],
      options: {
        chart: { type: "area", height: 200, toolbar: { show: false }, animations: { enabled: false } },
        colors: ["#94a3b8", "#f97316"],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        fill: { type: "gradient", gradient: { opacityFrom: 0.3, opacityTo: 0.05 } },
        xaxis: { type: "datetime", labels: { style: { fontSize: "11px" } } },
        yaxis: { title: { text: "PKR/day" }, labels: { formatter: (v) => PKR.format(v) } },
        legend: { position: "top", horizontalAlign: "right", fontSize: "12px" },
        grid: { borderColor: "#e2e8f0", strokeDashArray: 3 },
        tooltip: {
          x: { format: "dd MMM yyyy" },
          y: { formatter: (v) => (v == null ? "no data" : `PKR ${PKR.format(v)}/day`) },
        },
      },
    };
  }, [insightLoss]);

  if (!insightLoss) return null;

  const rows = insightLoss.per_insight || [];
  const measured = rows.filter((r) => r.measurable);
  const unmeasured = rows.filter((r) => !r.measurable);

  if (!insightLoss.available) {
    return (
      <div className="ei-insightloss">
        <p className="ei-detail-label">Insight loss</p>
        <div className="ei-alert info">
          <strong>Not measurable yet.</strong>{" "}
          <span style={{ fontWeight: 400 }}>{insightLoss.reason}</span>
        </div>
      </div>
    );
  }

  const t = insightLoss.totals;
  const regressed = t.worsened_count + t.emerged_count;

  return (
    <div className="ei-insightloss">
      <p className="ei-detail-label">Insight loss</p>

      {/* One headline line instead of four tiles. */}
      <div className="ei-loss-headline">
        <div>
          <span
            className={`ei-loss-figure ${toneClass(
              t.net_annualised_reduction_pkr
            )}`}
          >
            {fmtSigned(t.net_annualised_reduction_pkr)}
          </span>
          <span className="ei-loss-unit">/year</span>
        </div>
        <div className="ei-loss-meta">
          {fmtPkr(t.loss_reduction_pkr_per_day)}/day ·{" "}
          {/* "0 of 1 resolved" reads as a failure to fix something. When nothing
              fired in EITHER window there was nothing to resolve, so say that
              instead of scoring it. */}
          {t.absent_count === t.measurable_insights
            ? `no insight detected in either period`
            : `${t.resolved_count} of ${t.measurable_insights} insight${
                t.measurable_insights === 1 ? "" : "s"
              } resolved`}
          {regressed > 0 && (
            <>
              {" · "}
              <span className="ei-bad">{regressed} moved the wrong way</span>
            </>
          )}
        </div>
      </div>

      {/* Four columns, not seven. Before/after per-day moved into Details. */}
      <div className="ei-table-wrapper">
        <table className="ei-table ei-loss-table">
          <thead>
            <tr>
              <th>Insight</th>
              <th>Outcome</th>
              <th className="ei-num">Change</th>
              <th className="ei-num">Per year</th>
            </tr>
          </thead>
          <tbody>
            {measured.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.unknown;
              return (
                <tr key={r.insight_key} className="ei-row">
                  <td>
                    <div className="ei-title-cell">{r.name}</div>
                  </td>
                  <td>
                    <span className={`ei-badge loss-${meta.tone}`}>{meta.label}</span>
                  </td>
                  <td className="ei-num">
                    <span className={toneClass(r.annualised_loss_reduction_pkr)}>
                      {fmtPct(r.percent_reduction)}
                    </span>
                  </td>
                  <td className="ei-num">
                    <strong className={toneClass(r.annualised_loss_reduction_pkr)}>
                      {fmtSigned(r.annualised_loss_reduction_pkr)}
                    </strong>
                  </td>
                </tr>
              );
            })}
            {/* Listed, not hidden: an initiative where only 2 of 5 insights could
                be measured is a weaker claim than one where all 5 were. */}
            {unmeasured.map((r) => (
              <tr key={r.insight_key} className="ei-row">
                <td>
                  <div className="ei-title-cell">{r.name}</div>
                </td>
                <td>
                  <span className="ei-badge loss-flat" title={r.reason}>
                    Not measured
                  </span>
                </td>
                <td className="ei-num">—</td>
                <td className="ei-num">—</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button className="ei-basis-toggle" onClick={() => setShowDetails((v) => !v)}>
        {showDetails ? "▾" : "▸"} Details
      </button>

      {showDetails && (
        <Details insightLoss={insightLoss} measured={measured} chart={chart} />
      )}
    </div>
  );
}

/**
 * Everything needed to audit the headline, none of it needed to read it.
 *
 * This is where the window totals live. They are genuinely useful — they are what
 * the Insights tab reports for the same periods — but they are not comparable
 * across two windows of different lengths, so they never appear without the
 * per-day pair and the covered-day counts beside them.
 */
function Details({ insightLoss, measured, chart }) {
  const w = insightLoss.windows;
  const t = insightLoss.totals;
  const n = insightLoss.node;
  const errors = insightLoss.detector_errors || {};

  return (
    <div className="ei-basis-body">
      {w && (
        <p>
          <strong>Periods:</strong> before {w.baseline.start} → {w.baseline.end} (
          {w.baseline.covered_days} days monitored) · after {w.after.start} →{" "}
          {w.after.end} ({w.after.covered_days} days monitored)
          {n ? ` · ${n.node_name} and ${n.meters_considered} meter(s) beneath it` : ""}.
        </p>
      )}

      {measured.length > 0 && (
        <>
          <p>
            <strong>Per day vs window totals.</strong> The per-day pair is the
            comparable one — the two periods are different lengths, so the totals
            are not.
          </p>
          <table className="ei-mini-table">
            <thead>
              <tr>
                <td />
                <td className="ei-num">Before</td>
                <td className="ei-num">After</td>
              </tr>
            </thead>
            <tbody>
              {measured.map((r) => (
                <React.Fragment key={r.insight_key}>
                  <tr>
                    <td>{r.name} — PKR/day</td>
                    <td className="ei-num">
                      {r.before?.production_loss_pkr_per_day == null
                        ? "—"
                        : PKR.format(r.before.production_loss_pkr_per_day)}
                    </td>
                    <td className="ei-num">
                      {r.after?.production_loss_pkr_per_day == null
                        ? "—"
                        : PKR.format(r.after.production_loss_pkr_per_day)}
                    </td>
                  </tr>
                  <tr>
                    <td className="ei-hint">…window total (not comparable)</td>
                    <td className="ei-num ei-hint">
                      {PKR.format(r.before?.production_loss_pkr ?? 0)}
                    </td>
                    <td className="ei-num ei-hint">
                      {PKR.format(r.after?.production_loss_pkr ?? 0)}
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </>
      )}

      {(t.fixed_risk_eliminated_pkr > 0 || t.fixed_risk_added_pkr > 0) && (
        <p>
          <strong>Risk no longer carried:</strong> {fmtPkr(t.fixed_risk_eliminated_pkr)}{" "}
          of equipment and maintenance exposure fell away with the resolved
          insight(s). This is <em>not</em> in the saving above — the loss model
          charges it once per detection, not per hour, so it is exposure rather
          than measured money.
        </p>
      )}

      {chart && (
        <>
          <p>
            <strong>Trend</strong>
            {insightLoss.trends.coarsened &&
              ` (${insightLoss.trends.grain}, not ${insightLoss.trends.requested_grain} — too many periods)`}
            {insightLoss.trends.partial_buckets > 0 &&
              " · gaps are periods where a detector failed, not zero loss"}
          </p>
          <ReactApexChart
            options={chart.options}
            series={chart.series}
            type="area"
            height={200}
          />
        </>
      )}

      {Object.keys(errors).length > 0 && (
        <p>
          <strong>Detector failures:</strong>{" "}
          {Object.entries(errors)
            .map(([where, message]) => `${where} (${message})`)
            .join(", ")}
          . The insights they own are reported as unmeasurable, not as zero loss.
        </p>
      )}

      {(insightLoss.warnings || []).length > 0 && (
        <p>
          <strong>Notes:</strong> {insightLoss.warnings.join("; ")}.
        </p>
      )}
    </div>
  );
}
